import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, TreePine } from "lucide-react";
import api from "@/lib/api";

const EMPTY = { name_kz: "", name_ru: "", venue_type: "table", number: 1, status: "active", sort_order: 0 };

export default function AdminVenues() {
  const [venues, setVenues] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/venues").then(r => setVenues(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const tapshans = venues.filter(v => v.venue_type === "tapshan");
  const tables = venues.filter(v => v.venue_type === "table");

  const handleSave = async () => {
    const data = { ...form, number: +form.number, sort_order: +form.sort_order };
    if (editId) await api.put(`/admin/venues/${editId}`, data);
    else await api.post("/admin/venues", data);
    setDialogOpen(false);
    setForm(EMPTY);
    setEditId(null);
    load();
  };

  const handleEdit = (v) => {
    setForm({ ...v });
    setEditId(v.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить?")) return;
    await api.delete(`/admin/venues/${id}`);
    load();
  };

  const VenueCard = ({ v }) => (
    <div className={`bg-card border p-3 flex items-center justify-between ${v.status === "active" ? "border-white/10" : "border-red-500/30 opacity-60"}`} data-testid={`venue-${v.id}`}>
      <div>
        <p className="font-medium text-sm">{v.name_ru || v.name_kz}</p>
        <p className="text-xs text-muted-foreground">{v.name_kz}</p>
      </div>
      <div className="flex gap-1">
        <Button size="icon" variant="ghost" onClick={() => handleEdit(v)} className="w-7 h-7" data-testid={`edit-venue-${v.id}`}>
          <Pencil className="w-3 h-3" />
        </Button>
        <Button size="icon" variant="ghost" onClick={() => handleDelete(v.id)} className="w-7 h-7 text-red-400" data-testid={`delete-venue-${v.id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-venues">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Летник</h1>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="add-venue-btn">
              <Plus className="w-4 h-4" /> Добавить
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-sm">
            <DialogHeader><DialogTitle className="font-serif">{editId ? "Редактировать" : "Добавить"}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <Select value={form.venue_type} onValueChange={v => setForm({ ...form, venue_type: v })}>
                <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="venue-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="tapshan">Тапчан</SelectItem>
                  <SelectItem value="table">Столик</SelectItem>
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Название (KZ)" value={form.name_kz} onChange={e => setForm({ ...form, name_kz: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="venue-name-kz" />
                <Input placeholder="Название (RU)" value={form.name_ru} onChange={e => setForm({ ...form, name_ru: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="venue-name-ru" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Номер" value={form.number || ""} onChange={e => setForm({ ...form, number: e.target.value === "" ? 0 : parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" />
                <Input type="number" placeholder="Сортировка" value={form.sort_order || ""} onChange={e => setForm({ ...form, sort_order: e.target.value === "" ? 0 : parseInt(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" />
              </div>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-background border-white/10 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="active">Активен</SelectItem>
                  <SelectItem value="disabled">Выключен</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSave} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="venue-save-btn">Сохранить</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-8">
        <div>
          <h2 className="text-lg font-serif mb-3 flex items-center gap-2"><TreePine className="w-4 h-4 text-primary" /> Тапчаны ({tapshans.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {tapshans.map(v => <VenueCard key={v.id} v={v} />)}
          </div>
        </div>
        <div>
          <h2 className="text-lg font-serif mb-3">Столики ({tables.length})</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {tables.map(v => <VenueCard key={v.id} v={v} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
